/**
 * Document Management Hooks
 *
 * React hooks for document operations including upload, verification,
 * requirements checking, and document listing
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import type { Database } from "../lib/database.types";
import {
  getBucketForDocumentType,
  getDocumentBucket,
} from "../utils/documentStorage";
import { getRequirementsWithStatus } from "../utils/documentLogic";

const getSafeSession = supabase.getSafeSession;

export type DocumentCategory = "LICENSES" | "OHS" | "INSURANCE" | "ID";
export type DocumentType =
  Database["public"]["Tables"]["trade_documents"]["Row"]["document_type"];
export type TradeDocument =
  & Database["public"]["Tables"]["trade_documents"]["Row"]
  & {
    document_type_definitions?: { display_name: string } | null;
  };
export type DocumentRequirement =
  & Database["public"]["Tables"]["document_requirements"]["Row"]
  & {
    // Manual override if types aren't regenerated yet
    requirement_scope?: "COMPANY" | "INDIVIDUAL" | null;
    document_type_definitions?: { display_name: string } | null;
  };

export type DocumentRequirementWithStatus = DocumentRequirement & {
  latest_document: TradeDocument | null;
};

/**
 * Hook to fetch document requirements for a specific category
 */
export function useDocumentRequirements(category?: DocumentCategory, companyType?: 'TRADE' | 'CLIENT') {
  return useQuery({
    queryKey: ["document-requirements", category, companyType],
    queryFn: async () => {
      let query = supabase
        .from("document_requirements")
        .select("*, document_type_definitions(display_name)")
        .order("is_mandatory", { ascending: false })
        .order("document_type");

      // Filter by company_type (defaults to TRADE for backward compatibility)
      if (companyType) {
        query = query.eq('company_type', companyType);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Filter by category if provided (done client-side since there's no category column)
      if (category && data) {
        const categoryPatterns: Record<DocumentCategory, RegExp[]> = {
          LICENSES: [/^TRADE_LICENSE_/],
          OHS: [
            /^(WHITE_CARD|WORKING_AT_HEIGHTS|CONFINED_SPACE|ASBESTOS_AWARENESS|FIRST_AID_CERTIFICATE)$/,
          ],
          INSURANCE: [/_INSURANCE$/],
          ID: [/^(DRIVERS_LICENSE|PASSPORT|PROOF_OF_AGE_CARD)$/],
        };

        const patterns = categoryPatterns[category] || [];
        return data.filter((req: DocumentRequirement) =>
          patterns.some((pattern) => pattern.test(req.document_type))
        );
      }

      return data as DocumentRequirement[];
    },
    staleTime: 300000, // 5 minutes - requirements don't change often
  });
}

/**
 * Hook to fetch document requirements with upload status for a company
 * This joins requirements with uploaded documents to show what's been uploaded
 * and filters by company specializations.
 *
 * If profileId is provided, INDIVIDUAL requirements will resolve against that profile's documents.
 * COMPANY requirements resolve against company documents (regardless of who uploaded them).
 *
 * If userSpecializations is provided, it overrides company specializations for filtering
 * requirements. This allows per-user specialization-driven document requirements.
 */
export function useDocumentRequirementsWithStatus(
  companyId: string | null,
  category?: DocumentCategory,
  profileId?: string | null,
  userSpecializations?: string[] | null,
  companyType?: 'TRADE' | 'CLIENT',
) {
  const effectiveCompanyType = companyType ?? 'TRADE';
  const { data: requirements } = useDocumentRequirements(category, effectiveCompanyType);
  const { data: documents } = useCompanyDocuments(companyId, {
    includeProfileDocs: !!profileId,
    teamMemberIds: profileId ? [profileId] : undefined,
  });

  return useQuery<DocumentRequirementWithStatus[]>({
    queryKey: [
      "document-requirements-with-status",
      companyId,
      category,
      profileId,
      userSpecializations,
      effectiveCompanyType,
      requirements,
      documents,
    ],
    queryFn: async () => {
      if (!requirements || !companyId) return [];

      if (typeof getSafeSession === "function") {
        const session = await getSafeSession();
        if (!session) return [];
      }

      // Get company specializations to filter requirements
      const { data: company, error: companyError } = await supabase
        .from("trade_companies")
        .select("specializations, states_of_operation, business_state")
        .eq("id", companyId)
        .maybeSingle();

      if (companyError) throw companyError;

      if (!company && !userSpecializations) {
        return [];
      }

      // Use user-level specializations if provided, otherwise fall back to company
      const effectiveSpecializations = userSpecializations ??
        company?.specializations ?? [];

      // Multi-state: prefer states_of_operation, fall back to ARRAY[business_state]
      const operatingStates = company?.states_of_operation ??
        (company?.business_state ? [company.business_state] : null);

      return getRequirementsWithStatus(
        requirements,
        documents || [],
        effectiveSpecializations,
        operatingStates,
        profileId || null,
        effectiveCompanyType,
      );
    },
    enabled: !!companyId && !!requirements,
  });
}

/**
 * Hook to fetch documents for a company
 * Optionally includes profile-owned documents from specified team members.
 */
export function useCompanyDocuments(
  companyId: string | null,
  filters?: {
    category?: DocumentCategory;
    documentType?: DocumentType;
    verificationStatus?:
      | "PENDING"
      | "APPROVED"
      | "REJECTED"
      | "EXPIRED"
      | "REQUESTED";
    includeProfileDocs?: boolean;
    teamMemberIds?: string[];
  },
) {
  return useQuery({
    queryKey: ["company-documents", companyId, filters],
    queryFn: async () => {
      if (!companyId) return [];

      if (typeof getSafeSession === "function") {
        try {
          const session = await getSafeSession();
          if (!session) return [];
        } catch (e) {
          if (e && typeof e === "object") {
            const err = e as { name?: string; message?: string; code?: string | number };
            if (
              err.name === "AbortError" ||
              (typeof err.message === "string" && err.message.includes("signal is aborted")) ||
              err.code === 20 ||
              err.code === "ABORT_ERR"
            ) {
              return [];
            }
          }
          throw e;
        }
      }

      let query = supabase
        .from("trade_documents")
        .select("*, document_type_definitions(display_name)")
        .eq("is_current", true)
        .order("upload_timestamp", { ascending: false });

      // Build the ownership filter
      if (filters?.includeProfileDocs && filters?.teamMemberIds?.length) {
        // Include both company docs AND profile-owned docs from team members
        query = query.or(
          `trade_company_id.eq.${companyId},and(profile_id.in.(${
            filters.teamMemberIds.join(",")
          }),owner_type.eq.PROFILE)`,
        );
      } else {
        // Default: only company-owned documents
        query = query.eq("trade_company_id", companyId);
      }

      if (filters?.category) {
        query = query.eq("compliance_category", filters.category);
      }

      if (filters?.documentType) {
        query = query.eq("document_type", filters.documentType);
      }

      if (filters?.verificationStatus) {
        query = query.eq("verification_status", filters.verificationStatus);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as TradeDocument[];
    },
    enabled: !!companyId,
    staleTime: 10000, // 10 seconds
  });
}

/**
 * Hook to upload a document
 *
 * NOTE: Dynamically selects bucket based on document type for better security.
 */
export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      companyId: string;
      file: File;
      documentType: DocumentType;
      category: DocumentCategory;
      expiryDate?: string;
      metadata?: Record<string, unknown>;
      profileId?: string;
    }) => {
      // 1. Upload file to storage
      const bucketName = getBucketForDocumentType(params.documentType);
      const fileExt = params.file.name.split(".").pop();
      const fileName = `${params.companyId}/${Date.now()}.${fileExt}`;
      const filePath = fileName;

      const { error: uploadError } = await supabase.storage
        .from(bucketName)
        .upload(filePath, params.file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // 2. Create document record
      const { data, error } = await supabase
        .from("trade_documents")
        .insert(
          {
            // check_exactly_one_owner constraint: exactly one of trade_company_id/profile_id must be set
            trade_company_id: params.profileId ? null : params.companyId,
            profile_id: params.profileId || null,
            owner_type: params.profileId ? "PROFILE" : "COMPANY",
            document_type: params.documentType,
            compliance_category: params.category,
            file_name: params.file.name,
            file_path: filePath,
            file_size: params.file.size,
            mime_type: params.file.type,
            expiry_date: params.expiryDate || null,
            extracted_data: params.metadata
              ? JSON.parse(JSON.stringify(params.metadata))
              : null,
            bucket_id: bucketName, // Store the bucket name for future retrieval
          } as never,
        ) // Cast to bypass RejectExcessProperties for joined field artifacts
        .select()
        .single();

      if (error) throw error;
      return data as TradeDocument;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      if (data.trade_company_id) {
        queryClient.invalidateQueries({
          queryKey: ["company-documents", data.trade_company_id],
        });
      }
      // For PROFILE docs, trade_company_id is null — look up the user's company to invalidate
      if (data.profile_id) {
        supabase.from('profiles').select('trade_company_id').eq('id', data.profile_id).single()
          .then(({ data: profile }) => {
            if (profile?.trade_company_id) {
              queryClient.invalidateQueries({
                queryKey: ["company-documents", profile.trade_company_id],
              });
              queryClient.invalidateQueries({
                queryKey: ["document-compliance", profile.trade_company_id],
              });
            }
          });
      }
      queryClient.invalidateQueries({
        queryKey: ["document-compliance", data.trade_company_id],
      });
      // Also invalidate requirements-with-status (broadly, since profileId varies)
      queryClient.invalidateQueries({
        queryKey: ["document-requirements-with-status"],
        exact: false,
      });
    },
  });
}

/**
 * Hook to delete a document
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: number) => {
      // Get document details first
      const { data: document, error: fetchError } = await supabase
        .from("trade_documents")
        .select("*")
        .eq("id", documentId)
        .single();

      if (fetchError) throw fetchError;

      // Delete from storage
      // Use bucket_id from document if available, otherwise fallback to trade-documents
      const bucketName = getDocumentBucket({
        bucket_id: (document as Record<string, unknown>).bucket_id as
          | string
          | null
          | undefined,
        document_type: document.document_type,
      });

      const { error: storageError } = await supabase.storage
        .from(bucketName)
        .remove([document.file_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from("trade_documents")
        .delete()
        .eq("id", documentId);

      if (dbError) throw dbError;

      return document;
    },
    onSuccess: (document) => {
      // Invalidate the specific company's documents cache
      if (document.trade_company_id) {
        queryClient.invalidateQueries({
          queryKey: ["company-documents", document.trade_company_id],
        });
      }
      // Also do a broad invalidation — profile-owned documents have
      // trade_company_id=null, but the admin view queries by the
      // user's company. A broad invalidation ensures the UI always
      // refreshes after a delete (#15).
      queryClient.invalidateQueries({
        queryKey: ["company-documents"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["client-documents"],
        exact: false,
      });
      // Also invalidate compliance and requirements-with-status caches
      // so the compliance dashboard updates after deletion
      queryClient.invalidateQueries({
        queryKey: ["document-requirements-with-status"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["document-compliance"],
        exact: false,
      });
    },
  });
}

/**
 * Hook to check document compliance status
 */
export function useDocumentCompliance(companyId: string | null) {
  const { data: requirements } = useDocumentRequirements(undefined, 'TRADE');
  const { data: documents } = useCompanyDocuments(companyId);

  return useQuery({
    queryKey: ["document-compliance", companyId, requirements, documents],
    queryFn: async () => {
      if (typeof getSafeSession === "function") {
        const session = await getSafeSession();
        if (!session) {
          return {
            totalRequired: 0,
            uploaded: 0,
            verified: 0,
            missing: [] as string[],
            expired: [] as string[],
            expiringSoon: [] as string[],
            pending: [] as string[],
            completionPercentage: 0,
          };
        }
      }

      if (!requirements || !documents || !companyId) {
        return {
          totalRequired: 0,
          uploaded: 0,
          verified: 0,
          missing: [] as string[],
          expired: [] as string[],
          expiringSoon: [] as string[],
          pending: [] as string[],
          completionPercentage: 0,
        };
      }

      // Fetch company specializations and operating states to properly scope requirements
      const { data: company, error: companyError } = await supabase
        .from("trade_companies")
        .select("specializations, states_of_operation, business_state")
        .eq("id", companyId!)
        .maybeSingle();

      if (companyError || !company) {
        if (companyError) {
          console.error("Failed to fetch trade company for compliance scoping:", companyError);
        }
        // If we cannot determine scoping (e.g. for customer companies or when RLS blocks access),
        // we intentionally return an empty compliance state (totalRequired = 0) to avoid generating
        // incorrect missing counts or blocking non-trade company accounts.
        return {
          totalRequired: 0,
          uploaded: 0,
          verified: 0,
          missing: [] as string[],
          expired: [] as string[],
          expiringSoon: [] as string[],
          pending: [] as string[],
          completionPercentage: 0,
        };
      }

      const specializations = company?.specializations ?? [];
      const operatingStates = company?.states_of_operation
        ?? (company?.business_state ? [company.business_state] : []);

      // Filter requirements to only those relevant to this company
      const companyScoped = requirements.filter((r) => {
        // Only COMPANY-scoped (or null scope) requirements — personal docs handled elsewhere
        if (r.requirement_scope === 'INDIVIDUAL') return false;
        // Filter by trade type (specialization)
        if (r.trade_type && specializations.length > 0 && !specializations.includes(r.trade_type)) return false;
        if (r.trade_type && specializations.length === 0) return false;
        // Filter by state
        if (r.state) {
          if (!operatingStates.length) return false;
          if (!operatingStates.includes(r.state)) return false;
        }
        return true;
      });

      // Deduplicate mandatory requirements by document_type
      const seenDocTypes = new Set<string>();
      const mandatoryRequirements = companyScoped
        .filter((r) => r.is_mandatory)
        .filter((r) => {
          if (seenDocTypes.has(r.document_type)) return false;
          seenDocTypes.add(r.document_type);
          return true;
        });

      const now = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(now.getDate() + 30);

      const missing = new Set(mandatoryRequirements.map((r) => r.document_type));
      const expired = new Set<string>();
      const expiringSoon = new Set<string>();
      const pending = new Set<string>();
      const verified = new Set<string>();

      documents.forEach(doc => {
        // Skip docs that aren't for mandatory requirements (unless we want to track them anyway, but compliance only cares about mandatory)
        if (!mandatoryRequirements.some(r => r.document_type === doc.document_type)) return;

        const isExpired = doc.expiry_date && new Date(doc.expiry_date) < now;
        
        if (isExpired) {
            expired.add(doc.document_type);
        } else {
            if (doc.verification_status !== "REJECTED") {
                missing.delete(doc.document_type);
            }

            if (doc.verification_status === "APPROVED" || doc.verification_status === "VERIFIED") {
                verified.add(doc.document_type);
                if (doc.expiry_date) {
                    const expDate = new Date(doc.expiry_date);
                    if (expDate <= thirtyDaysFromNow) {
                        expiringSoon.add(doc.document_type);
                    }
                }
            } else if (doc.verification_status === "PENDING") {
                pending.add(doc.document_type);
            }
        }
      });

      const totalRequired = mandatoryRequirements.length;
      const completionPercentage = totalRequired > 0
        ? Math.min(100, Math.round((verified.size / totalRequired) * 100))
        : 100;

      return {
        totalRequired,
        uploaded: documents.length,
        verified: verified.size,
        missing: Array.from(missing),
        expired: Array.from(expired),
        expiringSoon: Array.from(expiringSoon),
        pending: Array.from(pending),
        completionPercentage,
      };
    },
    enabled: !!companyId && !!requirements && !!documents,
  });
}

/**
 * Hook to get document upload statistics by category
 */
export function useDocumentStatsByCategory(companyId: string | null) {
  const { data: documents } = useCompanyDocuments(companyId);

  return useQuery({
    queryKey: ["document-stats-by-category", companyId, documents],
    queryFn: async () => {
      if (!documents) {
        return {
          LICENSES: { total: 0, verified: 0, pending: 0 },
          OHS: { total: 0, verified: 0, pending: 0 },
          INSURANCE: { total: 0, verified: 0, pending: 0 },
          ID: { total: 0, verified: 0, pending: 0 },
        };
      }

      const stats = {
        LICENSES: { total: 0, verified: 0, pending: 0 },
        OHS: { total: 0, verified: 0, pending: 0 },
        INSURANCE: { total: 0, verified: 0, pending: 0 },
        ID: { total: 0, verified: 0, pending: 0 },
      };

      documents.forEach((doc) => {
        const category = doc.compliance_category as DocumentCategory;
        if (category in stats) {
          stats[category].total++;
          if (doc.verification_status === "APPROVED") {
            stats[category].verified++;
          } else if (doc.verification_status === "PENDING") {
            stats[category].pending++;
          }
        }
      });

      return stats;
    },
    enabled: !!companyId && !!documents,
  });
}

export default {
  useDocumentRequirements,
  useDocumentRequirementsWithStatus,
  useCompanyDocuments,
  useUploadDocument,
  useDeleteDocument,
  useDocumentCompliance,
  useDocumentStatsByCategory,
};
